// src/components/PIIAnonymizerPage/PIIAnonymizationOptionsDialog/DefaultTemplatesTab.jsx
import React, { useEffect } from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTranslation } from 'react-i18next';

const TemplateRow = ({ template, selected, onSelect }) => {
  const { t } = useTranslation();
  const { name, description } = template;

  return (
    <Paper
      variant="outlined"
      onClick={onSelect}
      sx={(theme) => ({
        p: 1.5,
        mb: 1,
        borderRadius: 2,
        cursor: 'pointer',
        borderColor: selected ? '#00e676' : 'divider',
        borderWidth: selected ? 2 : 1,
        boxShadow: selected ? '0 0 0 2px rgba(0, 230, 118, 0.4)' : 'none',
        bgcolor: 'background.paper',
        transition: theme.transitions.create(
          ['box-shadow', 'border-color', 'transform'],
          { duration: theme.transitions.duration.shorter }
        ),
        '&:hover': {
          transform: 'translateY(-1px)',
          borderColor: selected ? '#00e676' : 'primary.main',
          boxShadow: selected ? '0 0 0 3px rgba(0, 230, 118, 0.3)' : 3,
        },
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1.5,
      })}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }} noWrap>
          {name}
        </Typography>

        {description && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mt: 0.35,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {description}
          </Typography>
        )}
      </Box>

      {selected && (
        <Typography
          variant="caption"
          sx={{ color: '#00e676', fontWeight: 600, letterSpacing: 0.3 }}
        >
          {t('DefaultTemplatesTab.selected')}
        </Typography>
      )}
    </Paper>
  );
};

const DefaultTemplatesTab = ({
  selectedTemplateId,
  onSelectTemplate,
  loading = false,
  error = '',
}) => {
  const { t } = useTranslation();

  const templates = [
    {
      id: 'crz1',
      name: t('DefaultTemplatesTab.templates.crz1.name'),
      description: t('DefaultTemplatesTab.templates.crz1.desc'),
    },
    {
      id: 'crz_full',
      name: t('DefaultTemplatesTab.templates.crzFull.name'),
      description: t('DefaultTemplatesTab.templates.crzFull.desc'),
    },
    {
      id: 'document', // ✅ Updated ID to match your new Python backend profile
      name: t('DefaultTemplatesTab.templates.document.name'), // ✅ Updated i18n key
      description: t('DefaultTemplatesTab.templates.document.desc'), // ✅ Updated i18n key
    },
  ];

  useEffect(() => {
    if (!loading && !error && !selectedTemplateId && templates.length) {
      onSelectTemplate(templates[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, error, selectedTemplateId]);

  if (loading) {
    return (
      <Typography variant="body2" color="text.secondary">
        {t('DefaultTemplatesTab.loading')}
      </Typography>
    );
  }

  if (error) {
    return (
      <Typography variant="body2" color="error">
        {error}
      </Typography>
    );
  }

  return (
    <>
      <Box sx={{ mb: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {t('DefaultTemplatesTab.hint')}
        </Typography>
      </Box>

      {templates.map((tpl) => (
        <TemplateRow
          key={tpl.id}
          template={tpl}
          selected={selectedTemplateId === tpl.id}
          onSelect={() => onSelectTemplate(tpl.id)}
        />
      ))}
    </>
  );
};

export default DefaultTemplatesTab;